import React, { useState, FormEvent, ChangeEvent } from 'react';
import styles from './Contact.module.css';

interface FormState {
  name:    string;
  email:   string;
  subject: string;
  message: string;
}

interface DirectLink {
  icon:   string;
  label:  string;
  value:  string;
  href:   string;
}

const DIRECT_LINKS: DirectLink[] = [
  { icon: '✉',  label: 'Email',    value: 'tharangamahavila@gmail.com', href: 'mailto:tharangamahavila@gmail.com' },
  { icon: '📞', label: 'Phone',    value: '+46 769 768 743',             href: 'tel:+46769768743' },
  { icon: '🔗', label: 'LinkedIn', value: 'tharanga-mahavila',           href: 'https://www.linkedin.com/in/tharanga-mahavila-7b9994199' },
  { icon: '💻', label: 'GitHub',   value: 'TharangaMahavila',            href: 'https://github.com/TharangaMahavila' },
];

const INITIAL_FORM: FormState = { name: '', email: '', subject: '', message: '' };

export default function Contact(): React.ReactElement {
  const [form, setForm]       = useState<FormState>(INITIAL_FORM);
  const [sent, setSent]       = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const { name, email, subject, message } = form;
    const mailto = `mailto:tharangamahavila@gmail.com?subject=${encodeURIComponent(subject || 'Portfolio Contact')}&body=${encodeURIComponent(`Name: ${name}\nEmail: ${email}\n\n${message}`)}`;
    window.location.href = mailto;
    setTimeout(() => { setLoading(false); setSent(true); }, 1000);
  };

  return (
    <section id="contact" className={styles.section}>
      <div className={styles.inner}>
        <h2 className={styles.heading}>
          <span className={styles.headingNum}>05.</span>
          Contact
          <div className={styles.headingLine} />
        </h2>

        <p className={styles.intro}>
          I'm currently open to Senior / Lead Engineering roles in Europe.
          Whether you have an opportunity or just want to say hello — my inbox is always open.
        </p>

        <div className={styles.grid}>
          {/* Direct links column */}
          <div>
            <p className={styles.colTitle}>Reach me directly</p>
            {DIRECT_LINKS.map(({ icon, label, value, href }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" className={styles.directLink}>
                <span className={styles.linkIcon} aria-hidden="true">{icon}</span>
                <div className={styles.linkInfo}>
                  <div className={styles.linkLabel}>{label}</div>
                  <div className={styles.linkValue}>{value}</div>
                </div>
                <span className={styles.linkArrow} aria-hidden="true">→</span>
              </a>
            ))}
          </div>

          {/* Form column */}
          <div>
            <p className={styles.colTitle}>Send a message</p>

            {sent ? (
              <div className={styles.success}>
                <div className={styles.successIcon} aria-hidden="true">✓</div>
                <p className={styles.successText}>Your email client should have opened!</p>
                <button className={styles.resetBtn} onClick={() => { setSent(false); setForm(INITIAL_FORM); }}>
                  Send another
                </button>
              </div>
            ) : (
              <form className={styles.form} onSubmit={handleSubmit} noValidate>
                <div className={styles.formRow}>
                  <input
                    className={styles.input}
                    name="name"
                    placeholder="Your name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    autoComplete="name"
                  />
                  <input
                    className={styles.input}
                    name="email"
                    type="email"
                    placeholder="Your email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    autoComplete="email"
                  />
                </div>
                <input
                  className={styles.input}
                  name="subject"
                  placeholder="Subject"
                  value={form.subject}
                  onChange={handleChange}
                />
                <textarea
                  className={styles.textarea}
                  name="message"
                  placeholder="Your message..."
                  required
                  value={form.message}
                  onChange={handleChange}
                />
                <button type="submit" className={styles.submit} disabled={loading}>
                  {loading ? 'Opening...' : 'Send Message →'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
